import { Fragment, useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import { dateinameAusHeader, widerrufeSpaeter } from '../download';
import PlentyUebersicht from './PlentyUebersicht';
import { empfaengerErgebnisText, konversationText, laufUmleitungText, mailStatusAnzeige, schreibZusammenfassung } from '../lauf';
import { detailAbschnitte, historieZeile, konversationsZeilen, zeitText } from '../historie';
import { zahl } from '../formate';
import type { HistorieEintrag, ProtokollLauf } from '../typen';

/**
 * Letzte Laeufe (Protokoll aus daily_order_laeufe). Alle Texte aus dem Backend werden als
 * React-Textknoten gerendert, der Mailtext (HTML) wird nicht gezeigt, nur der Betreff.
 */
export default function Historie({ neuLadenSchluessel }: { neuLadenSchluessel: number }) {
  const [eintraege, setEintraege] = useState<HistorieEintrag[]>([]);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<number | null>(null);
  const [detail, setDetail] = useState<ProtokollLauf | null>(null);
  const [detailFehler, setDetailFehler] = useState<string | null>(null);
  const [downloadFehler, setDownloadFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api
      .get('/programmes/daily-order/historie', { params: { limit: 20 } })
      .then((res) => {
        if (!aktiv) return;
        setEintraege(res.data?.data?.eintraege ?? []);
        setHinweis(res.data?.data?.hinweis ?? null);
        setLadeFehler(null);
      })
      .catch((err) => {
        if (aktiv) setLadeFehler(err.response?.data?.error?.message ?? 'Historie konnte nicht geladen werden');
      });
    return () => {
      aktiv = false;
    };
  }, [neuLadenSchluessel]);

  const umschalten = async (id: number) => {
    if (offen === id) {
      setOffen(null);
      setDetail(null);
      return;
    }
    setOffen(id);
    setDetail(null);
    setDetailFehler(null);
    setDownloadFehler(null);
    try {
      const res = await api.get('/programmes/daily-order/historie/' + id);
      setDetail(res.data.data);
    } catch (err: any) {
      setDetailFehler(err.response?.data?.error?.message ?? 'Details konnten nicht geladen werden');
    }
  };

  const herunterladen = async (id: number, lieferantId: string, ersatz: string) => {
    setDownloadFehler(null);
    try {
      const res = await api.get('/programmes/daily-order/historie/' + id + '/datei/' + lieferantId, {
        responseType: 'blob',
      });
      const name = dateinameAusHeader(String(res.headers?.['content-disposition'] ?? ''), ersatz);
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      widerrufeSpaeter(url);
    } catch {
      setDownloadFehler('Datei konnte nicht heruntergeladen werden');
    }
  };

  const d = detail ? detailAbschnitte(detail) : null;

  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold text-[#1A1A1A]">Letzte Läufe</h2>
      {ladeFehler && <p className="p-3 rounded text-sm bg-red-50 text-red-800">{ladeFehler}</p>}
      {hinweis && <p className="p-3 rounded text-sm bg-yellow-50 text-yellow-900">{hinweis}</p>}
      {!hinweis && !ladeFehler && eintraege.length === 0 && <p className="text-sm text-gray-500">Noch keine Läufe</p>}
      {eintraege.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-[#DAD9D9]">
            <thead className="bg-[#DAD9D9] text-[#1A1A1A]">
              <tr>
                <th className="px-3 py-2 text-left">Zeit</th>
                <th className="px-3 py-2 text-left">Referenz</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Lieferanten</th>
                <th className="px-3 py-2 text-left">Gestartet von</th>
              </tr>
            </thead>
            <tbody>
              {eintraege.map((e) => {
                const z = historieZeile(e);
                return (
                  <Fragment key={e.id}>
                    <tr
                      className="border-t border-[#DAD9D9] cursor-pointer hover:bg-gray-50"
                      onClick={() => umschalten(e.id)}
                    >
                      <td className="px-3 py-2">{z.zeit}</td>
                      <td className="px-3 py-2">{z.referenz}</td>
                      <td className="px-3 py-2">
                        {z.testmodus && (
                          <span
                            className="mr-1 px-2 py-0.5 rounded text-xs font-semibold bg-[#1A1A1A] text-[#E9FF57]"
                            title={
                              z.testlauf
                                ? 'Testlauf (Status 3.4): alle Mails gingen an die Testadresse, Plenty-Write nur für Aufträge mit @anstoss24.de-Kunden-Mail'
                                : 'Testlauf: alle Mails gingen an die Testadresse, nichts nach Plenty geschrieben'
                            }
                          >
                            TEST
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded text-xs ${z.status.klasse}`}>{z.status.text}</span>
                      </td>
                      <td className="px-3 py-2 space-x-2">
                        {z.lieferanten.map((l) => (
                          <span key={l.name} className="inline-flex items-center gap-1">
                            {l.name}
                            <span className={`px-2 py-0.5 rounded text-xs ${l.mailStatus.klasse}`}>
                              {l.mailStatus.text}
                            </span>
                            <span className="text-gray-600">({zahl(l.positionen)})</span>
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2">{z.gestartetVon}</td>
                    </tr>
                    {offen === e.id && (
                      <tr className="border-t border-[#DAD9D9] bg-gray-50">
                        <td colSpan={5} className="px-3 py-3 space-y-3">
                          {detailFehler && <p className="p-3 rounded text-sm bg-red-50 text-red-800">{detailFehler}</p>}
                          {!d && !detailFehler && <p className="text-gray-500">Lädt...</p>}
                          {detail && d && (
                            <>
                              {detail.fehler && (
                                <p className="p-3 rounded text-sm bg-red-50 text-red-800">{detail.fehler}</p>
                              )}
                              {laufUmleitungText(detail.ergebnis) && (
                                <p className="p-3 rounded text-sm bg-yellow-50 text-yellow-900">
                                  {laufUmleitungText(detail.ergebnis)}
                                </p>
                              )}
                              {detail.ergebnis?.absender?.hinweis && (
                                <p className="p-3 rounded text-sm bg-yellow-50 text-yellow-900">
                                  {detail.ergebnis.absender.hinweis}
                                </p>
                              )}
                              {(detail.ergebnis?.warnungen ?? []).map((w, i) => (
                                <p key={i} className="p-3 rounded text-sm bg-yellow-50 text-yellow-900">
                                  {w}
                                </p>
                              ))}
                              <table className="min-w-full text-sm border border-[#DAD9D9] bg-white">
                                <thead className="bg-[#DAD9D9] text-[#1A1A1A]">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Lieferant</th>
                                    <th className="px-3 py-2 text-left">Empfänger</th>
                                    <th className="px-3 py-2 text-left">Betreff</th>
                                    <th className="px-3 py-2 text-left">Datei</th>
                                    <th className="px-3 py-2 text-left">FreeScout</th>
                                    <th className="px-3 py-2 text-right">Positionen</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <th className="px-3 py-2 text-left">Fehler</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {d.lieferanten.map((l) => {
                                    return (
                                      <tr key={l.id} className="border-t border-[#DAD9D9]">
                                        <td className="px-3 py-2">{l.name}</td>
                                        <td className="px-3 py-2">{empfaengerErgebnisText(l, d.testmodus)}</td>
                                        <td className="px-3 py-2">{l.betreff ?? '—'}</td>
                                        <td className="px-3 py-2">{l.dateiname ?? '—'}</td>
                                        <td className="px-3 py-2">{konversationText(l)}</td>
                                        <td className="px-3 py-2 text-right">{zahl(l.positionen)}</td>
                                        <td className="px-3 py-2">{mailStatusAnzeige(l.mailStatus).text}</td>
                                        <td className="px-3 py-2">{l.fehler ?? '—'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              {d.konversationen.length > 0 && (
                                <div>
                                  <strong>FreeScout-Konversationen je Auftrag:</strong>
                                  <ul className="list-disc pl-5">
                                    {konversationsZeilen(
                                      d.konversationen,
                                      Object.fromEntries(d.lieferanten.map((l) => [l.id, l.name])),
                                    ).map((t) => (
                                      <li key={t}>{t}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {d.dateien.length > 0 && (
                                <div className="space-y-1">
                                  {d.dateien.map((f) => (
                                    <div key={f.lieferantId} className="flex items-center gap-3">
                                      <span>
                                        {f.lieferantName}: {f.dateiname}
                                      </span>
                                      <Button
                                        onClick={() => herunterladen(e.id, f.lieferantId, f.dateiname)}
                                        className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
                                      >
                                        <Download className="h-4 w-4 mr-2" />
                                        Datei herunterladen
                                      </Button>
                                    </div>
                                  ))}
                                  {downloadFehler && (
                                    <p className="p-3 rounded text-sm bg-red-50 text-red-800">{downloadFehler}</p>
                                  )}
                                </div>
                              )}
                              <p>
                                <strong>Versendet:</strong>{' '}
                                {d.auftraegeVersendet.join(', ') || '—'}
                              </p>
                              <p>
                                <strong>Mit Fehlschlag:</strong> {d.auftraegeMitFehlschlag.join(', ') || '—'}
                              </p>
                              {d.auftraegeVersandUnklar.length > 0 && (
                                <p className="p-2 rounded bg-orange-50 text-orange-900">
                                  <strong>Versand unklar:</strong> {d.auftraegeVersandUnklar.join(', ')} — in FreeScout (info@)
                                  prüfen, ob die Bestellung raus ist; dann manuell
                                </p>
                              )}
                              <PlentyUebersicht schreiben={d.schreiben} z={schreibZusammenfassung(d.schreiben, d.mailsGesendet, d.quellStatus)} />
                              <div>
                                <strong>Bleibt in {String(d.quellStatus)}:</strong>
                                {d.bleibtInStatus.length === 0 ? (
                                  <span> —</span>
                                ) : (
                                  <ul className="list-disc pl-5">
                                    {d.bleibtInStatus.map((b) => (
                                      <li key={b.orderId}>
                                        {b.orderId}: {b.grund}{b.detail ? ` — ${b.detail}` : ''}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                Gestartet {zeitText(detail.gestartetAm)}, beendet {zeitText(detail.beendetAm)}
                              </p>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
